import { useState } from "react";
import { Copy, Check, Send } from "lucide-react";
import { motion } from "framer-motion";

const Contact = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText("contact@anantmishra.in");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Animation Variants for Staggered Reveals
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <section id="contact" className="relative py-8 px-4 md:px-8 bg-slate-900 overflow-hidden">
      
      {/* --- THE AURORA BACKGROUND --- */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ x: [0, 100, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px]"
        />
        <motion.div 
          animate={{ x: [0, -100, 0], y: [0, 50, 0], scale: [1, 1.5, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[120px]"
        />
      </div>

      <div className="max-w-6xl mx-auto w-full relative z-10 grid lg:grid-cols-5 gap-12 lg:gap-24 items-start py-0">
        
        {/* --- LEFT: The Text Reveal --- */}
        <div className="lg:col-span-2 text-white">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false }}
              variants={containerVariants}
            >
                <motion.div variants={itemVariants} className="flex items-center gap-4 mb-8">
                    <span className="h-px w-12 bg-blue-500"></span>
                    <span className="text-blue-400 font-bold tracking-widest uppercase text-sm">Contact</span>
                </motion.div>
                
                <h2 className="text-6xl md:text-7xl font-bold tracking-tighter mb-8 leading-[1.1]">
                  {/* Word-by-Word Stagger Animation */}
                  <span className="block overflow-hidden">
                    <motion.span variants={itemVariants} className="block">Let's start a</motion.span>
                  </span>
                  <span className="block overflow-hidden">
                    <motion.span 
                      variants={itemVariants} 
                      className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400"
                    >
                      conversation.
                    </motion.span>
                  </span>
                </h2>

                <motion.p variants={itemVariants} className="text-slate-400 text-lg leading-relaxed mb-12">
                    Interested in working together? I’m always open to discussing product strategy, design systems, or partnership opportunities.
                </motion.p>

                {/* Interactive Email Card */}
                <motion.div 
                  variants={itemVariants}
                  onClick={handleCopy}
                  className="group cursor-pointer bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-6 transition-all duration-300 w-fit"
                >
                    <div className="flex items-center justify-between gap-8 mb-2">
                        <span className="text-xs font-bold uppercase text-slate-500 tracking-wider">Email Me</span>
                        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />}
                    </div>
                    <div className="text-xl font-mono text-white group-hover:text-blue-300 transition-colors">
                        contact@anantmishra.in
                    </div>
                </motion.div>
            </motion.div>
        </div>

        {/* --- RIGHT: The Cascading Form --- */}
        <div className="lg:col-span-3">
            <motion.div 
                initial={{ opacity: 0, y: 40, rotateX: -10 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: false, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden perspective-1000"
            >
                {/* Form Shine Effect */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                <motion.form 
                  className="space-y-8"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false }}
                  onSubmit={(e) => e.preventDefault()}
                >
                    <div className="grid md:grid-cols-2 gap-8">
                        <motion.div variants={itemVariants}>
                          <FloatingInput label="Your Name" />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                          <FloatingInput label="Email Address" type="email" />
                        </motion.div>
                    </div>
                    
                    <motion.div variants={itemVariants}>
                      <FloatingInput label="Project Subject" />
                    </motion.div>
                    
                    <motion.div variants={itemVariants} className="relative pt-4">
                        <textarea 
                          rows={4} 
                            className="peer w-full bg-transparent border-b border-white/20 text-white text-lg py-2 focus:border-blue-400 focus:outline-none transition-colors resize-none placeholder-transparent"
                            placeholder="Message"
                            id="message"
                        ></textarea>
                        <label 
                            htmlFor="message"
                            className="absolute left-0 -top-2 text-xs text-blue-400 font-bold uppercase tracking-wider transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-slate-500 peer-placeholder-shown:top-2 peer-focus:-top-2 peer-focus:text-xs peer-focus:text-blue-400"
                        >
                            Tell me about your project
                        </label>
                    </motion.div>

                    {/* The "Shimmer" Button */}
                    <motion.button 
                      type="submit"
                      variants={itemVariants}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="group relative w-full overflow-hidden rounded-xl bg-white text-slate-900 font-bold text-lg py-5 transition-all hover:bg-blue-500 hover:text-white"
                    >
                        {/* Shimmer Overlay */}
                        <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10" />
                        
                        <span className="relative z-20 flex items-center justify-center gap-3">
                            Send Message
                            <Send className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </span>
                    </motion.button>
                </motion.form>
            </motion.div>
        </div>

      </div>
    </section>
  );
};

const FloatingInput = ({ label, type = "text" }) => {
  // sanitize label to use as id (no spaces or special chars)
  const id = String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
  <div className="relative pt-4">
    <input 
      type={type} 
      className="peer w-full bg-transparent border-b border-white/20 text-white text-lg py-2 focus:border-blue-400 focus:outline-none transition-colors placeholder-transparent"
      placeholder={label}
      id={id}
    />
    <label 
      htmlFor={id}
      className="absolute left-0 -top-2 text-xs text-blue-400 font-bold uppercase tracking-wider transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-slate-500 peer-placeholder-shown:top-2 peer-focus:-top-2 peer-focus:text-xs peer-focus:text-blue-400"
    >
      {label}
    </label>
  </div>
  );
};

export default Contact;